import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import ProjectSetup from './ProjectSetup.jsx'
import Audience from './Audience.jsx'
import WorldBuilding from './WorldBuilding.jsx'
import PlotStructure from './PlotStructure.jsx'
import Characters from './Characters.jsx'
import RelationshipMap from './RelationshipMap.jsx'
import ChapterPlanner from './ChapterPlanner.jsx'
import Editor from './Editor.jsx'
import StyleVoice from './StyleVoice.jsx'
import Brainstorm from './Brainstorm.jsx'
import PlotPlanner from './PlotPlanner.jsx'
import WritingCommentsPanel from './WritingCommentsPanel.jsx'

const TABS = [
  { id: 'brainstorm',  label: 'Brainstorm' },
  { id: 'foundation',  label: 'Foundation' },
  { id: 'world',       label: 'The World' },
  { id: 'characters',  label: 'Characters' },
  { id: 'plot',        label: 'Plot' },
  { id: 'structure',   label: 'Structure' },
  { id: 'voice',       label: 'Voice' },
  { id: 'write',       label: '✦ Write' },
]

function PlaceholderCard({ title, body }) {
  return (
    <div style={{
      background: 'var(--surf)',
      borderRadius: '14px',
      padding: '2.5rem 1.5rem',
      textAlign: 'center',
    }}>
      <p style={{ fontFamily: 'var(--font-serif)', fontSize: '1.05rem', color: 'var(--bark)', marginBottom: '0.6rem', margin: '0 0 0.6rem' }}>
        {title}
      </p>
      <p style={{ fontSize: '0.85rem', color: 'var(--stone)', lineHeight: '1.7', maxWidth: '400px', margin: '0 auto' }}>
        {body}
      </p>
    </div>
  )
}

export default function Writing() {
  const [projects,   setProjects]   = useState([{ id: 'w', name: 'My Story' }])
  const [activePid,  setActivePid]  = useState('w')
  const [writeChapId, setWriteChapId] = useState('general')
  const [pendingQuote, setPendingQuote] = useState(null)
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const t = p.get('tab')
    return TABS.find(x => x.id === t) ? t : 'brainstorm'
  })

  useEffect(() => {
    api.getWritingProjects().then(d => { if (d?.length) setProjects(d) })
    api.getActiveProject().then(d => { if (d) setActivePid(d) })
  }, [])
  const [showNewProject, setShowNewProject] = useState(false)
  const [newName, setNewName] = useState('')

  const pid = activePid || 'w'

  function goTab(id) {
    setActiveTab(id)
    history.replaceState(null, '', `?tab=${id}`)
  }

  function addProject() {
    const name = newName.trim()
    if (!name) return
    const id = 'w' + Date.now()
    const next = [...projects, { id, name }]
    setProjects(next)
    setActivePid(id)
    api.saveWritingProjects(next)
    api.setActiveProject(id)
    setNewName('')
    setShowNewProject(false)
    goTab('brainstorm')
  }

  function switchProject(id) {
    setActivePid(id)
    api.setActiveProject(id)
    goTab('brainstorm')
  }

  return (
    <div className="page active" id="page-writing">
      <div className="page-inner">
        <div className="hero">
          <p className="hero-eyebrow">Your writing companion</p>
          <h1>Tell your <em>story</em></h1>
          <hr className="hero-rule" />
          <SectionWelcome text="Your stories start here. A space to think, plan, and write without pressure." />
        </div>

        {/* ── Project switcher ─────────────────────────────────── */}
        <div className="w-project-bar">
          {projects.map(p => (
            <button
              key={p.id}
              className={`w-project-chip${p.id === pid ? ' active' : ''}`}
              onClick={() => switchProject(p.id)}
            >
              {p.name}
            </button>
          ))}

          {!showNewProject ? (
            <button
              className="w-project-chip w-project-add"
              onClick={() => setShowNewProject(true)}
            >
              + New project
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="w-input"
                style={{ width: 170, padding: '4px 10px', fontSize: '0.82rem' }}
                placeholder="Project name..."
                value={newName}
                autoFocus
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') addProject()
                  if (e.key === 'Escape') { setShowNewProject(false); setNewName('') }
                }}
              />
              <button className="save-btn ready" onClick={addProject} style={{ padding: '5px 14px', fontSize: '0.8rem' }}>
                Add
              </button>
              <button className="ghost-btn" onClick={() => { setShowNewProject(false); setNewName('') }}>
                ✕
              </button>
            </div>
          )}
        </div>

        {/* ── Tab navigation ───────────────────────────────────── */}
        <div className="cooking-tabs" style={{ marginBottom: '1.25rem' }}>
          {TABS.map(t => (
            <React.Fragment key={t.id}>
              {t.id === 'write' && (
                <span style={{
                  width: 1, height: 24,
                  background: 'rgba(0,0,0,0.12)',
                  margin: '0 2px',
                  alignSelf: 'center',
                  flexShrink: 0,
                }} />
              )}
              <button
                className={`cooking-tab${activeTab === t.id ? ' active' : ''}`}
                onClick={() => goTab(t.id)}
              >
                {t.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* ── Tab content ──────────────────────────────────────── */}
        {activeTab === 'brainstorm' && (
          <Brainstorm
            pid={pid}
            projectName={projects.find(p => p.id === pid)?.name || 'My Story'}
          />
        )}

        {activeTab === 'foundation' && (
          <>
            <ProjectSetup pid={pid} />
            <Audience pid={pid} />
          </>
        )}

        {activeTab === 'world' && (
          <WorldBuilding pid={pid} />
        )}

        {activeTab === 'characters' && (
          <>
            <Characters pid={pid} />
            <RelationshipMap pid={pid} />
          </>
        )}

        {activeTab === 'plot' && (
          <PlotPlanner pid={pid} />
        )}

        {activeTab === 'structure' && (
          <ChapterPlanner pid={pid} />
        )}

        {activeTab === 'voice' && (
          <StyleVoice pid={pid} />
        )}

        {activeTab === 'write' && (
          <div style={{ position: 'relative', display: 'flex', gap: 0, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Editor
                pid={pid}
                onChapChange={setWriteChapId}
                onAddNote={text => { setPendingQuote(text); setCommentsPanelOpen(true) }}
              />
            </div>
            <WritingCommentsPanel
              pid={pid}
              chapId={writeChapId}
              open={commentsPanelOpen}
              onToggle={() => setCommentsPanelOpen(v => !v)}
              pendingQuote={pendingQuote}
              onClearPending={() => setPendingQuote(null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
